import { useCallback, useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, ButtonGroup } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { Menu, MenuItem, IconButton } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { setGlobalBrand as setGlobalBrandAction } from "@/shared/store/brandSlice";
import { reloadOtherTabs } from "@/shared/helpers/authHelper";
import { GlobalContext } from "@/helpers/contexts/contexts";
import ImageRP from "@/components/ImageRP";
import {
  userBrandsSelector,
  selectedBrandSelector,
} from "@/shared/store/brandSlice";
import { USER_ROLE } from "@/config/constants";
import { useAppTheme } from "@/helpers/contexts/themeContext";
import { loginWithToken } from "@/shared/helpers/tokenBasedLoginHelper";
import { getDashboardPath } from "@/helpers/utils";

const hideBrandToggleOnRoute = [
  "/subscription-packages",
  "/dashboard/digital-campaigns/",
  "/dashboard/location-report/",
  "/participant/join-campaign/",
  "/location-management",
  "/thank-you",
];

type BrandOption = {
  brand_logo?: string;
  brand_name?: string;
  id: number | string;
};

type BrandState = {
  brand?: {
    selectedBrand?: BrandOption | null;
    userBrands?: BrandOption[];
  };
};

type HeaderBrandContext = {
  loggedInUser?: {
    logo_name?: string;
  } | null;
  loggedInUserRole?: string;
};

type AppThemeContext = {
  getTheme: (_brandId: BrandOption["id"]) => void;
};

const selectUserBrands = userBrandsSelector as unknown as (
  _state: unknown
) => BrandOption[] | undefined;
const selectSelectedBrand = selectedBrandSelector as unknown as (
  _state: unknown
) => BrandOption | null | undefined;
const ButtonGroupComponent = ButtonGroup as unknown as React.ComponentType<{
  children: React.ReactNode;
  className?: string;
}>;
const ButtonComponent = Button as unknown as React.ComponentType<{
  children: React.ReactNode;
  className?: string;
  variant?: string;
}>;

const HeaderBrandLogoDropDown = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { getTheme } = useAppTheme() as AppThemeContext;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const brands = useSelector((state: BrandState) => selectUserBrands(state));
  const selectedBrandFromRedux = useSelector((state: BrandState) =>
    selectSelectedBrand(state)
  );
  const setGlobalBrand = useCallback(
    (brand: BrandOption) => {
      reloadOtherTabs();
      dispatch(setGlobalBrandAction(brand));
    },
    [dispatch]
  );

  const { loggedInUserRole, loggedInUser } = useContext(
    GlobalContext
  ) as HeaderBrandContext;

  const selectedBrand = selectedBrandFromRedux && selectedBrandFromRedux;

  /**
   * @method handleOnClickBrand
   * @description handle on click brand
   * @param {*} brand
   */
  const handleOnClickBrand = (brand: BrandOption) => {
    setGlobalBrand(brand);
    loginWithToken({ data: brand });
    getTheme(brand.id);
    setAnchorEl(null);
  };

  const handleToggleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (anchorEl) {
      // If menu is open, close it
      setAnchorEl(null);
    } else {
      // If menu is closed, open it
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const renderDropdownToggle = () => {
    if (!brands || brands.length === 0 || brands.length === 1) {
      return null;
    } else if (hideBrandToggleOnRoute.some((r) => pathname.search(r) > -1)) {
      return null;
    } else if (
      loggedInUserRole === USER_ROLE.BRAND_OWNER.NAME ||
      loggedInUserRole === USER_ROLE.BRAND_USER.NAME
    ) {
      return null;
    }

    const open = Boolean(anchorEl);

    return (
      <>
        <IconButton
          id="dropdown-basic"
          onClick={handleToggleClick}
          title="Switch Brand"
          size="small"
          aria-label="Switch Brand"
          style={{ height: "100%" }}
        >
          <ArrowDropDownIcon
            style={{ fontSize: "16px", color: "var(--neutral-700)" }}
          />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          className="header-brand-logo-drop-down-menu mt-2 ml-3"
          transformOrigin={{ horizontal: "center", vertical: "top" }}
          anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        >
          {brands?.map((brand) => (
            <MenuItem
              key={brand?.id}
              onClick={() => handleOnClickBrand(brand)}
              selected={brand?.id === selectedBrand?.id}
              title={brand?.brand_name}
              className="dropdown-item"
            >
              {brand?.brand_name}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  };

  return (
    <ButtonGroupComponent className="header-brand-logo-drop-down">
      <Link to={`${getDashboardPath()}`} className="navbar-brand mr-0">
        <ButtonComponent variant="default" className="p-0 brand-image">
          <ImageRP
            src={
              loggedInUserRole === USER_ROLE.BRAND_OWNER.NAME ||
              loggedInUserRole === USER_ROLE.BRAND_USER.NAME
                ? loggedInUser?.logo_name
                : selectedBrand && selectedBrand?.brand_logo
            }
            type="BRAND"
            title={(selectedBrand && selectedBrand?.brand_name) || ""}
          />
        </ButtonComponent>
      </Link>

      {renderDropdownToggle()}
    </ButtonGroupComponent>
  );
};

export default HeaderBrandLogoDropDown;
